import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/contexts/CartContext";
import { useLang, pickLocalized } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Cart() {
  const { items, remove, update, total, count } = useCart();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    shipping_name: user && user.name ? user.name : "",
    shipping_email: user && user.email ? user.email : "",
    shipping_phone: "",
    shipping_address: "",
    shipping_city: "",
    shipping_postal_code: "",
    shipping_country: "España",
  });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Estimación de gastos de envío según el código postal
  const [shipping, setShipping] = useState(null); // {cost, free, zone} | null
  useEffect(() => {
    const cp = form.shipping_postal_code.trim();
    if (cp.length < 5 || total <= 0) {
      setShipping(null);
      return;
    }
    const timer = setTimeout(() => {
      api.get(`/shipping/quote`, { params: { cp, subtotal: total } })
        .then((r) => setShipping(r.data))
        .catch(() => setShipping(null));
    }, 400); // debounce
    return () => clearTimeout(timer);
  }, [form.shipping_postal_code, total]);

  const grandTotal = total + (shipping?.cost || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        origin_url: window.location.origin,
        ...form,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  if (count === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center fade-in" data-testid="cart-empty">
        <h1 className="font-serif-display text-5xl">{t("cart.title")}</h1>
        <p className="text-[var(--eafg-muted)] mt-6">{t("cart.empty")}</p>
        <Link
          to="/tienda"
          className="inline-block mt-8 bg-[var(--eafg-ink)] text-white px-7 py-4 tracking-eyebrow hover:bg-[var(--eafg-copper)] transition-colors"
        >
          {t("cart.emptyCta")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 fade-in" data-testid="cart-page">
      <h1 className="font-serif-display text-5xl md:text-6xl mb-12" data-testid="cart-title">
        {t("cart.title")}
      </h1>

      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7 space-y-6">
          {items.map((it) => (
            <div
              key={it.product_id}
              className="grid grid-cols-12 gap-4 pb-6 border-b border-[var(--eafg-line)]"
              data-testid={`cart-item-${it.product_id}`}
            >
              <div className="col-span-3 aspect-square overflow-hidden bg-[#efece8]">
                {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="col-span-9 flex flex-col justify-between">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif-display text-2xl">
                      {lang === "es" ? it.name_es : it.name_en}
                    </h3>
                    <p className="text-[var(--eafg-muted)] mt-1">{it.price.toFixed(2)} €</p>
                  </div>
                  <button
                    onClick={() => remove(it.product_id)}
                    className="text-[var(--eafg-muted)] hover:text-[var(--eafg-copper)]"
                    data-testid={`cart-remove-${it.product_id}`}
                    aria-label="remove"
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center border border-[var(--eafg-ink)]">
                    <button
                      onClick={() => update(it.product_id, it.quantity - 1)}
                      className="px-3 py-2 hover:bg-[var(--eafg-ink)] hover:text-white"
                      data-testid={`cart-qty-dec-${it.product_id}`}
                    >
                      <Minus size={12} strokeWidth={1.5} />
                    </button>
                    <span className="px-4 tabular-nums">{it.quantity}</span>
                    <button
                      onClick={() => update(it.product_id, it.quantity + 1)}
                      className="px-3 py-2 hover:bg-[var(--eafg-ink)] hover:text-white"
                      data-testid={`cart-qty-inc-${it.product_id}`}
                    >
                      <Plus size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                  <span className="font-medium tabular-nums">
                    {(it.price * it.quantity).toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="md:col-span-5">
          <form
            onSubmit={submit}
            className="border border-[var(--eafg-line)] p-8 space-y-5 bg-white"
            data-testid="cart-checkout-form"
          >
            <h2 className="font-serif-display text-3xl">{t("cart.shippingDetails")}</h2>
            <Field label={t("cart.shippingName")} required value={form.shipping_name} onChange={onChange("shipping_name")} testId="ship-name" />
            <Field label={t("cart.shippingEmail")} type="email" required value={form.shipping_email} onChange={onChange("shipping_email")} testId="ship-email" />
            <Field label={t("cart.shippingPhone")} value={form.shipping_phone} onChange={onChange("shipping_phone")} testId="ship-phone" />
            <Field label={t("cart.shippingAddress")} required value={form.shipping_address} onChange={onChange("shipping_address")} testId="ship-address" />
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("cart.shippingCity")} required value={form.shipping_city} onChange={onChange("shipping_city")} testId="ship-city" />
              <Field label={t("cart.shippingPostalCode")} required value={form.shipping_postal_code} onChange={onChange("shipping_postal_code")} testId="ship-postal" />
            </div>
            <Field label={t("cart.shippingCountry")} value={form.shipping_country} onChange={onChange("shipping_country")} testId="ship-country" />

            <div className="border-t border-[var(--eafg-line)] pt-5 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="tracking-eyebrow text-[var(--eafg-muted)]">{t("cart.base")}</span>
                <span className="tabular-nums text-[var(--eafg-muted)]" data-testid="cart-base">
                  {(total / 1.21).toFixed(2)} €
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="tracking-eyebrow text-[var(--eafg-muted)]">{t("cart.iva")} (21%)</span>
                <span className="tabular-nums text-[var(--eafg-muted)]" data-testid="cart-iva">
                  {(total - total / 1.21).toFixed(2)} €
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="tracking-eyebrow text-[var(--eafg-muted)]">Envío</span>
                <span className="tabular-nums text-[var(--eafg-muted)]" data-testid="cart-shipping">
                  {shipping === null
                    ? "introduce tu CP"
                    : shipping.free
                    ? "GRATIS"
                    : `${shipping.cost.toFixed(2)} €`}
                </span>
              </div>
              <div className="flex items-center justify-between text-lg pt-2 border-t border-[var(--eafg-line)]">
                <span className="tracking-eyebrow">{t("cart.total")}</span>
                <span className="font-medium tabular-nums" data-testid="cart-total">
                  {grandTotal.toFixed(2)} €
                </span>
              </div>
              <p className="text-[10px] tracking-eyebrow text-[var(--eafg-muted)] text-right">
                {t("cart.ivaIncluded")}
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--eafg-copper)] text-white py-4 tracking-eyebrow hover:bg-[var(--eafg-copper-dark)] transition-colors disabled:opacity-50"
              data-testid="cart-pay-button"
            >
              {submitting ? t("cart.processing") : t("cart.pay")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <label className="block">
      <span className="tracking-eyebrow text-[var(--eafg-muted)] block mb-2">{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full border-b border-[var(--eafg-line)] bg-transparent py-2 focus:outline-none focus:border-[var(--eafg-ink)]"
        data-testid={`field-${testId}`}
      />
    </label>
  );
}
